<template>
  <div>
    <div class="subtitle2">{{ $t("modals.addAccessPermissions.restrict.service.title") }}</div>
    <v-select
      v-model="serviceNameValue"
      :items="serviceOptions"
      class="mb-6 mt-4"
      :rules="[required()]"
      variant="outlined"
      :placeholder="$t('modals.addAccessPermissions.restrict.service.label')"
      :label="$t('modals.addAccessPermissions.restrict.service.label')"
    >
      <template #item="{ item, props }">
        <v-list-item v-bind="props" :title="item.raw.name">
          <template v-slot:prepend>
            <coro-icon class="service-icon mr-2" :icon-name="item.raw.value"></coro-icon>
          </template>
          <template v-slot:append>
            <v-tooltip location="top" open-delay="300" v-if="serviceHasIssues(item.raw.value)">
              <template #activator="{ props }">
                <v-icon icon="$warningTriangleXs" v-bind="props"></v-icon>
              </template>
              {{ $t("modals.addAccessPermissions.restrict.service.hasIssues") }}
            </v-tooltip>
          </template>
        </v-list-item>
      </template>
      <template #selection="{ item }">
        <div class="d-flex w-100 justify-space-between">
          <span class="d-flex align-center">
            <coro-icon class="service-icon mr-2" :icon-name="item.raw.value"></coro-icon>
            {{ item.raw.name }}
          </span>
          <v-tooltip
            location="top"
            v-if="serviceHasIssues(item.raw.value)"
            class="body2"
            open-delay="300"
          >
            <template #activator="{ props }">
              <v-icon icon="$warningTriangleXs" v-bind="props"></v-icon>
            </template>
            {{ $t("modals.addAccessPermissions.restrict.service.hasIssues") }}
          </v-tooltip>
        </div>
      </template>
    </v-select>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import CoroIcon from "@/components/CoroIcon.vue";
import { required } from "@/_helpers/validators";
import { Service, ServiceStatus } from "@/constants/cloud-apps";
import type { ServiceSelection } from "@/_store/cloud-apps/access-permissions.module";
import { serviceHasMissingRequiredPermissions } from "@/_helpers/cloud-security.ts";
import { RouteName } from "@/constants/routes.ts";
import { useCloudAppsStore } from "@/_store/cloud-apps/cloud-apps.module.ts";
import { useI18n } from "vue-i18n";

export default defineComponent({
  components: {
    CoroIcon,
  },
  props: {
    serviceName: {
      type: String as PropType<ServiceSelection>,
      required: true,
    },
  },
  emits: ["update:serviceName"],
  setup(props, { emit }) {
    const i18n = useI18n();
    const serviceNameValue = computed({
      get: () => props.serviceName,
      set: (value: ServiceSelection) => emit("update:serviceName", value),
    });

    const cloudAppsStore = useCloudAppsStore();

    const serviceHasIssues = (serviceName: Service) => {
      const serviceObj = cloudAppsStore.services.find((svc) => svc.name === serviceName);

      if (!serviceObj) return false;

      const criticalStatuses = [ServiceStatus.INCOMPLETE, ServiceStatus.DISCONNECTED];

      if (criticalStatuses.includes(serviceObj.serviceStatus)) {
        return true;
      }

      const isO365MissingAuditLogs =
        serviceObj.name === Service.OFFICE_365 && !serviceObj.auditLogEnabled;

      if (isO365MissingAuditLogs) {
        return true;
      }

      if (serviceObj.permissions?.length) {
        return serviceHasMissingRequiredPermissions(
          serviceObj,
          RouteName.CLOUD_SECURITY_PERMISSIONS_LIST_TAB
        );
      }

      return false;
    };

    const serviceOptions = computed(() => [
      ...cloudAppsStore.services
        .filter((service) => service.serviceStatus !== ServiceStatus.NOT_CONNECTED)
        .map((service) => ({
          name: i18n.t(`services.${service.name}`),
          value: service.name,
        })),
    ]);

    return {
      required,
      serviceNameValue,
      serviceHasIssues,
      serviceOptions,
    };
  },
});
</script>

<style lang="scss" scoped>
.service-icon {
  height: 24px;
  width: 24px;
}
</style>
